'use client';

import { ArrowRight, HousePerson, NavArrowDown } from '@byway/icons';
import { type CalendarDate, DateFormatter, getLocalTimeZone, parseDate } from '@internationalized/date';
import { NumberFlowGroup } from '@number-flow/react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { DialogTrigger, Button as RACButton } from 'react-aria-components';
import { AnimatedPrice } from '~/components/AnimatedPrice';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import { Popover } from '~/components/Popover';
import { PromoBadge } from '~/components/PromoBadge';
import { ChangeOriginModal } from '~/components/TripOverview/booking/ChangeOriginModal';
import { ChangeTravellersModal } from '~/components/TripOverview/booking/ChangeTravellersModal';
import { TripDateError } from '~/components/TripOverview/booking/TripDateError';
import { TripLoyaltyPriceRow } from '~/components/TripOverview/booking/TripLoyaltyPriceRow';
import { TripPriceDetailsContent } from '~/components/TripOverview/booking/TripPriceDetailsContent';
import { TripPriceSkeleton } from '~/components/TripOverview/booking/TripPriceSkeleton';
import { useHasStayWithNoAlternatives } from '~/components/TripOverview/hooks/useHasStayWithNoAlternatives';
import { useTripDateSelection } from '~/components/TripOverview/hooks/useTripDateSelection';
import { useTripOriginUpdate } from '~/components/TripOverview/hooks/useTripOriginUpdate';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useTripPriceSummary } from '~/components/TripOverview/hooks/useTripPriceSummary';
import { useTripTravellersUpdate } from '~/components/TripOverview/hooks/useTripTravellersUpdate';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { getPathname } from '~/i18n/navigation';
import { PERCENT_DEPOSIT } from '~/types/Payment.types';
import { getCookie } from '~/utils/cookies';
import { getTripDurationDaysFromStayNights } from '~/utils/dateHelpers/calendarRange';
import { trackClickEvent, trackFunnelEvent } from '~/utils/funnelTracking';
import { pricePerPerson as pricePerPersonForPax } from '~/utils/paxHelpers';
import { shouldOfferDeposit } from '~/utils/paymentHelpers/paymentHelpers';
import { CURRENT_PROMO, hasAppliedFreeUpgradePromo } from '~/utils/promoHelper';
import styles from '../TripOverviewPage.module.css';
import { UNAVAILABLE_BANNER_ID } from './TripOverviewUnavailableBanner';

type TripOverviewDesktopBookingCardProps = {
  originLabel: string;
  datesLabel: string;
  travellersLabel: string;
  totalPerPersonLabel: string;
  nextAccommodationLabel: string;
};

const RangeCalendar = dynamic(() => import('~/components/RangeCalendar').then((m) => m.RangeCalendar), {
  ssr: false
});

const POPOVER_TRIGGER_TOP_OFFSET_PX = 80;

export const TripOverviewDesktopBookingCard = ({
  originLabel,
  datesLabel,
  travellersLabel,
  totalPerPersonLabel,
  nextAccommodationLabel
}: Readonly<TripOverviewDesktopBookingCardProps>) => {
  const t = useTranslations('journey');
  const tForms = useTranslations('forms');
  const locale = useLocale();
  const timeZone = getLocalTimeZone();

  const isTripUpdating = useIsTripUpdating();

  const {
    tripId,
    overviewData,
    firstItem,
    dateRange,
    nights,
    isReadOnly,
    isDateUnset,
    isStartDateInPast,
    isLiveChecking,
    isStartDateUnavailable
  } = useTripOverviewSummary();
  const isEditingLocked = isReadOnly || isLiveChecking;
  const isRequestToBook = !!overviewData?.requestToBook;
  const isSelectionLocked = isEditingLocked || isDateUnset;
  const hasStayWithNoAlternatives = useHasStayWithNoAlternatives();
  const { totalPrice, isPriceLoading } = useTripPriceSummary();
  const isUkCustomer = getCookie('displayCountry') === 'GB';
  const { isPromoLive } = CURRENT_PROMO();
  const showPromoBadge = isUkCustomer && isPromoLive && hasAppliedFreeUpgradePromo(overviewData?.appliedPromotions);

  const dayCount = getTripDurationDaysFromStayNights(nights);

  const {
    setLocalRange,
    range: calendarRange,
    calendarKey,
    hasSelectedRange,
    hasDateChange,
    originalDuration,
    datesConfirmed,
    dateError,
    confirmDates,
    clearDates
  } = useTripDateSelection();
  const tripEndDateLabel = new DateFormatter(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(calendarRange.end.toDate(timeZone));
  const savedStartDate = isDateUnset || !firstItem?.startDate ? null : parseDate(firstItem.startDate).toDate(timeZone);
  const offerDeposit = !isRequestToBook && (isDateUnset || shouldOfferDeposit(savedStartDate));
  const { confirmedOrigin, confirmedOriginLabel, confirmOrigin } = useTripOriginUpdate();
  const {
    selectedTravellerCount,
    selectedAdultCount,
    selectedChildCount,
    selectedRooms,
    adultCount,
    childAges,
    confirmTravellers
  } = useTripTravellersUpdate();

  const datesTriggerRef = useRef<HTMLButtonElement>(null);
  const travellersTriggerRef = useRef<HTMLButtonElement>(null);
  const targetScrollYRef = useRef<number | null>(null);

  const scrollOnceToTarget = (el: HTMLElement | null) => {
    if (!el || typeof window === 'undefined') return;
    const top = el.getBoundingClientRect().top;
    if (targetScrollYRef.current === null) {
      const delta = top - POPOVER_TRIGGER_TOP_OFFSET_PX;
      targetScrollYRef.current = delta > 0 ? window.scrollY + delta : window.scrollY;
      if (delta <= 0) return;
      window.scrollTo({ top: targetScrollYRef.current, behavior: 'instant' });
    } else if (window.scrollY < targetScrollYRef.current) {
      window.scrollTo({ top: targetScrollYRef.current, behavior: 'instant' });
    }
  };

  const [isDatesPopoverOpen, setIsDatesPopoverOpen] = useState(false);
  const handleDatesOpenChange = (open: boolean) => {
    if (open) {
      trackClickEvent('trip-page-edit-click-dates');
      scrollOnceToTarget(datesTriggerRef.current);
    }
    setIsDatesPopoverOpen(open);
  };
  const handleConfirmDates = async () => {
    if (!hasDateChange) {
      if (!datesConfirmed) {
        trackFunnelEvent('trip_funnel_confirm_dates', { location: 'booking_card' });
        void confirmDates();
      }
      setIsDatesPopoverOpen(false);
      return;
    }
    trackFunnelEvent('trip_funnel_confirm_dates', { location: 'booking_card' });
    const ok = await confirmDates();
    if (ok) setIsDatesPopoverOpen(false);
  };

  const [isOriginModalOpen, setIsOriginModalOpen] = useState(false);
  const openOriginModal = () => {
    trackClickEvent('trip-page-edit-click-origin');
    setIsOriginModalOpen(true);
  };

  const [isAccommodationWarm, setIsAccommodationWarm] = useState(false);

  const [isTravellersPopoverOpen, setIsTravellersPopoverOpen] = useState(false);
  const handleTravellersOpenChange = (open: boolean) => {
    if (open) {
      trackClickEvent('trip-page-edit-click-pax');
      scrollOnceToTarget(travellersTriggerRef.current);
    }
    setIsTravellersPopoverOpen(open);
  };

  const pricePerPerson =
    totalPrice !== undefined ? pricePerPersonForPax(totalPrice, selectedAdultCount, childAges) : undefined;

  return (
    <div className={styles.desktopBookingCard}>
      {showPromoBadge ? (
        <div className={styles.desktopBookingPromo}>
          <PromoBadge variant="inline" />
        </div>
      ) : null}

      <p className={styles.desktopBookingTripTitle}>{t('overview_booking_card_trip_duration', { count: dayCount })}</p>

      <div className={clsx(styles.desktopBookingGrid, isRequestToBook && styles.desktopBookingGridLocked)}>
        <RACButton
          isDisabled={isSelectionLocked}
          onPress={openOriginModal}
          className={`${styles.desktopBookingOrigin} ${styles.desktopBookingFieldButton}`}
        >
          <HousePerson decorative size="small" className={styles.desktopBookingOriginIcon} />
          <div className={styles.desktopBookingFieldText}>
            <span className={styles.desktopBookingLabel}>{originLabel}</span>
            <span className={styles.desktopBookingValue}>{confirmedOriginLabel}</span>
          </div>
          {!isReadOnly && (
            <NavArrowDown
              decorative
              size="small"
              className={
                isOriginModalOpen
                  ? `${styles.desktopBookingChevron} ${styles.desktopBookingChevronOpen}`
                  : styles.desktopBookingChevron
              }
            />
          )}
        </RACButton>
        <ChangeOriginModal
          isOpen={isOriginModalOpen}
          close={() => setIsOriginModalOpen(false)}
          currentOrigin={confirmedOrigin}
          tripId={tripId}
          onConfirm={confirmOrigin}
        />
        <DialogTrigger isOpen={isDatesPopoverOpen} onOpenChange={handleDatesOpenChange}>
          <RACButton
            ref={datesTriggerRef}
            isDisabled={isEditingLocked}
            aria-invalid={hasStayWithNoAlternatives || undefined}
            aria-describedby={hasStayWithNoAlternatives ? UNAVAILABLE_BANNER_ID : undefined}
            className={
              hasStayWithNoAlternatives
                ? `${styles.desktopBookingFieldHalf} ${styles.desktopBookingFieldButton} ${styles.desktopBookingFieldError}`
                : `${styles.desktopBookingFieldHalf} ${styles.desktopBookingFieldButton}`
            }
          >
            <div className={styles.desktopBookingFieldText}>
              <span className={styles.desktopBookingLabel}>{datesLabel}</span>
              <span className={styles.desktopBookingValue}>{dateRange}</span>
            </div>
            {!isReadOnly && (
              <NavArrowDown
                decorative
                size="small"
                className={
                  isDatesPopoverOpen
                    ? `${styles.desktopBookingChevron} ${styles.desktopBookingChevronOpen}`
                    : styles.desktopBookingChevron
                }
              />
            )}
          </RACButton>
          <Popover
            placement="bottom end"
            shouldFlip={false}
            isDismissable
            border={false}
            ariaLabel={datesLabel}
            maxWidth={860}
            style={{ padding: 0 }}
          >
            <div className={styles.calendarPopover}>
              {dateError && <TripDateError message={dateError} />}
              <RangeCalendar
                key={calendarKey}
                aria-label={datesLabel}
                monthsToShow={2}
                layout="horizontal"
                variant="connected"
                value={hasSelectedRange ? calendarRange : null}
                fixedDuration={originalDuration ?? undefined}
                isStartDateUnavailable={isStartDateUnavailable}
                onChange={(next) => {
                  if (!next?.start || !next.end) return;
                  const start = next.start as CalendarDate;
                  const end =
                    originalDuration != null ? start.add({ days: originalDuration - 1 }) : (next.end as CalendarDate);
                  setLocalRange({ start, end });
                }}
              />
              <div className={styles.calendarActions}>
                <button type="button" className={styles.calendarClear} onClick={clearDates}>
                  {t('booking_clear_dates')}
                </button>
                <Button
                  variant="secondary"
                  isPillShape
                  size="small"
                  onPress={handleConfirmDates}
                  isDisabled={isTripUpdating}
                >
                  {hasDateChange ? t('booking_save_changes') : t('continue')}
                </Button>
              </div>
            </div>
          </Popover>
        </DialogTrigger>
        <DialogTrigger isOpen={isTravellersPopoverOpen} onOpenChange={handleTravellersOpenChange}>
          <RACButton
            ref={travellersTriggerRef}
            isDisabled={isSelectionLocked}
            className={`${styles.desktopBookingFieldHalf} ${styles.desktopBookingFieldButton}`}
          >
            <div className={styles.desktopBookingFieldText}>
              <span className={styles.desktopBookingLabel}>{travellersLabel}</span>
              <span className={styles.desktopBookingValue}>
                {t('booking_travellers_summary', {
                  adults: selectedAdultCount,
                  children: selectedChildCount,
                  rooms: selectedRooms
                })}
              </span>
            </div>
            {!isReadOnly && (
              <NavArrowDown
                decorative
                size="small"
                className={
                  isTravellersPopoverOpen
                    ? `${styles.desktopBookingChevron} ${styles.desktopBookingChevronOpen}`
                    : styles.desktopBookingChevron
                }
              />
            )}
          </RACButton>
          <Popover
            placement="bottom end"
            shouldFlip={false}
            isDismissable
            border={false}
            ariaLabel={t('booking_choose_travellers')}
            maxWidth={520}
            style={{ padding: 0 }}
          >
            <ChangeTravellersModal
              key={`${adultCount}-${childAges.join(',')}-${selectedRooms}`}
              presentation="popover"
              isOpen
              close={() => setIsTravellersPopoverOpen(false)}
              adultCount={adultCount}
              initialChildAges={childAges}
              rooms={selectedRooms}
              tripEndDateLabel={tripEndDateLabel}
              onConfirm={confirmTravellers}
            />
          </Popover>
        </DialogTrigger>
      </div>

      {pricePerPerson === undefined && isPriceLoading && (
        <div className={styles.desktopBookingPriceRow}>
          <TripPriceSkeleton />
        </div>
      )}

      {pricePerPerson !== undefined && (
        <NumberFlowGroup>
          <div className={styles.desktopBookingPriceRow}>
            <DialogTrigger>
              <RACButton className={styles.desktopBookingPriceButton}>
                <TripLoyaltyPriceRow>
                  <AnimatedPrice amountGBP={pricePerPerson} />
                </TripLoyaltyPriceRow>
              </RACButton>
              <Popover
                placement="top end"
                isDismissable
                freeHeight
                hasArrow
                ariaLabel={totalPerPersonLabel}
                maxWidth={380}
              >
                <TripPriceDetailsContent
                  paxCount={selectedTravellerCount}
                  roomCount={selectedRooms}
                  pricePerPerson={pricePerPerson}
                />
              </Popover>
            </DialogTrigger>
            <span className={styles.desktopBookingPriceSuffix}>{t('sticky_footer_price_suffix')}</span>
          </div>

          {offerDeposit && (
            <div className={styles.desktopBookingUpfront}>
              {t.rich('overview_booking_card_deposit', {
                percent: PERCENT_DEPOSIT,
                price: () => (
                  <strong>
                    <AnimatedPrice amountGBP={Math.round(pricePerPerson * (PERCENT_DEPOSIT / 100))} />
                  </strong>
                )
              })}
            </div>
          )}
        </NumberFlowGroup>
      )}

      <div className={styles.desktopBookingCta}>
        {!isReadOnly && !datesConfirmed ? (
          <Button
            variant="primary"
            isPillShape
            size="small"
            onPress={() => {
              trackFunnelEvent('trip_funnel_confirm_dates_prompt', {
                location: 'booking_card'
              });
              handleDatesOpenChange(true);
            }}
            isDisabled={isTripUpdating || isLiveChecking}
          >
            {tForms('choose_dates')}
          </Button>
        ) : (
          <Link
            asButton="primary"
            size="small"
            href={getPathname({
              href: `/journey/accommodation/${tripId}`,
              locale
            })}
            isDisabled={isTripUpdating || isLiveChecking || isStartDateInPast}
            onClick={() =>
              trackFunnelEvent('trip_funnel_next_accommodation', {
                location: 'booking_card'
              })
            }
            prefetch={isAccommodationWarm && !isTripUpdating ? true : undefined}
            onPointerEnter={() => setIsAccommodationWarm(true)}
            onFocus={() => setIsAccommodationWarm(true)}
          >
            {nextAccommodationLabel} <ArrowRight decorative isInline size="small" />
          </Link>
        )}
      </div>
    </div>
  );
};
